import React, { useEffect, useRef, useState, useLayoutEffect, useImperativeHandle, useMemo, useCallback } from 'react';
import {
  InstUISettingsProvider,
  IamValidationProvider,
  IamValidationGuard,
  Flex,
  View
} from '@smarterservices/smarter-ui';
import { debounce } from '@instructure/debounce';
import SplitView from '../Common/SplitView';
import Header from '../Header';
import CONSTANTS from '../../config/constants';
import { useSessionPublicApi } from './useSessionPublicApi';
import {
  getStore,
  usePlayerStore,
  useAppStore,
  useLayoutStore,
  useExamSessionStore,
  useEventStore,
} from '../../stores/StoreContext';

import Sounds from '@ui/shared/components/Common/Sounds';

import VideoPlayer from '../Player';
import SessionOverlay from './Overlays';
import SessionDetailsWrapper from './SessionDetailsWrapper';
import DraggableAnnotationsModal from '../SessionDetails/Annotations/DraggableAnnotationsModal';
import { useDraggableModal } from '@ui/shared/components/Common/DraggableModal';
import { useLayoutHotkeys } from '../../hooks/useLayoutHotkeys';
import { useAudioOutputDevice } from '../../hooks/useAudioOutputDevice';
import { FullScreenProvider } from '../../contexts/FullScreenContext';
import { SessionLogModal, IncidentReportModal, SettingsModal, CheckinRequiredModal } from '../Modals';
import SessionDiagnostics from '../Common/SessionDiagnostics';
import TrackDebugModal from '../Common/TrackDebugModal';
import './styles.css';

/**
 * ReactSession - Main SessionViewer component for proctoring and session playback
 * 
 * This is the primary container component for the SessionViewer application. It orchestrates
 * the entire session viewing experience, including live proctoring and recorded session playback.
 * The component manages initialization, layout, IAM validation, keyboard shortcuts, modals, and
 * provides a public API for external control.
 * 
 * **Key Features:**
 * - **Dual Mode Support**: Handles both live proctoring and recorded session playback
 * - **Split View Layout**: Resizable vertical split between video player and session details
 * - **IAM Validation**: Enforces permission-based access control throughout the UI
 * - **Keyboard Shortcuts**: Comprehensive hotkey system for layout and playback control
 * - **Modal Management**: Coordinates multiple modal dialogs (annotations, logs, settings, etc.)
 * - **Audio Output Control**: Manages audio device selection across all media elements
 * - **Public API**: Exposes imperative methods for external control via ref
 * - **Performance Optimized**: Memoized components and callbacks to minimize re-renders
 * 
 * **Architecture:**
 * - Uses Zustand stores for state management (AppStore, ExamSessionStore, PlayerStore, etc.)
 * - Implements React.forwardRef for imperative API exposure
 * - Leverages custom hooks for layout hotkeys and audio device management
 * - Integrates with LiveKit for real-time media streaming
 * - Uses InstructureUI components for consistent styling
 * 
 * **Layout Structure:**
 * ```
 * InstUISettingsProvider
 *   └── FullScreenProvider
 *       └── IamValidationProvider
 *           └── IamValidationGuard
 *               └── SplitView (vertical)
 *                   ├── First Pane: Header + VideoPlayer
 *                   └── Second Pane: SessionDetails (collapsible)
 * ```
 * 
 * **Initialization Flow:**
 * 1. Component mounts and calls `initializeSession()`
 * 2. AppStore.reinitialize() runs parallel initialization (see AppStore docs)
 * 3. Layout elements are registered with LayoutStore
 * 4. IAM permissions are validated
 * 5. Session loaded event is triggered
 * 6. Loading overlay is removed after 200ms delay
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.apiUrl - Base URL for API requests
 * @param {string} props.roomId - LiveKit room ID for media connections
 * @param {string} props.spToken - Authentication token for API calls
 * @param {string} props.sessionSid - Session SID to load
 * @param {string} props.examSid - Exam SID associated with the session
 * @param {string} props.installSid - Installation SID for the session
 * @param {string} props.identity - User identity for LiveKit connections
 * @param {string} props.websocketUrl - WebSocket URL for LiveKit connections
 * @param {Object} [props.enabledComponents] - Component visibility configuration
 * @param {boolean|Object} [props.enabledComponents.sessionDetails] - Session details panel config
 * @param {boolean} [props.enabledComponents.sessionHeader] - Enable session header
 * @param {boolean} [props.enabledComponents.timeline] - Enable timeline component
 * @param {boolean} [props.enabledComponents.chat] - Enable chat component
 * @param {Object} [props.video] - Video configuration
 * @param {string[]} [props.video.sources] - Available video sources ('live', 'recorded')
 * @param {string} [props.video.default] - Default video source
 * @param {string} [props.type='instructor'] - User type (e.g., 'instructor', 'proctor')
 * @param {string} [props.viewMode='focus'] - Initial view mode ('focus' or 'overview')
 * @param {Object} [props.magnification] - Magnification settings
 * @param {boolean} [props.magnification.enabled=false] - Enable magnification
 * @param {number} [props.magnification.level=2] - Magnification level
 * @param {number} [props.magnification.lensSize=200] - Lens size in pixels
 * @param {string} [props.defaultSessionDetailsTab='flags'] - Default tab in session details
 * @param {string} [props.attachedToProctorRoomSid] - Proctor room SID if attached
 * @param {React.Ref} ref - Forwarded ref exposing public API methods
 * 
 * @returns {JSX.Element} The complete SessionViewer interface
 * 
 * @example
 * // Basic usage
 * const sessionRef = useRef();
 * 
 * <ReactSession
 *   ref={sessionRef}
 *   spToken="token_123"
 *   sessionSid="session_456"
 *   examSid="exam_789"
 *   installSid="install_abc"
 *   apiUrl="https://api.example.com"
 *   roomId="room_def"
 *   identity="user_ghi"
 *   websocketUrl="wss://ws.example.com"
 *   enabledComponents={{
 *     sessionDetails: { defaultTab: 'flags', defaultCollapsed: false },
 *     sessionHeader: true,
 *     chat: true
 *   }}
 *   video={{
 *     sources: ['live', 'recorded'],
 *     default: 'live'
 *   }}
 * />
 * 
 * @example
 * // Using the public API
 * sessionRef.current.play();
 * sessionRef.current.pause();
 * sessionRef.current.setPlaybackRate(1.5);
 * const state = sessionRef.current.getSessionState();
 * 
 * @see {@link useSessionPublicApi} For public API methods
 * @see {@link AppStore.initialize} For initialization process
 * @see {@link useLayoutHotkeys} For keyboard shortcuts
 */
export const ReactSession = React.forwardRef(function ReactSession({
  apiUrl,
  roomId,
  spToken,
  sessionSid,
  examSid,
  installSid,
  isReconnect,
  identity,
  websocketUrl,
  enabledComponents,
  video,
  type,
  viewMode,
  magnification,
  defaultSessionDetailsTab = 'flags',
  attachedToProctorRoomSid,
  focusedSessionSid,  // PR-3412: For VTC context focused session guard
  ...props
}, ref) {
  const EventStore = getStore('eventStore');
  const LayoutStore = getStore('layoutStore');
  const AppStore = getStore('appStore');
  const PlayerStore = getStore('playerStore');
  const ExamSessionStore = getStore('examSessionStore');
  const SettingsStore = getStore('settingsStore');

  // Memoize VideoPlayer to prevent unnecessary re-renders
  const MemoizedVideoPlayer = React.memo(VideoPlayer);

  // Wait for AppStore initialization before rendering main content
  const initialized = useAppStore((state) => state.initialized);

  // Sound state for Sounds component
  // Sound management for audio notifications
  const soundToPlay = useAppStore((state) => state.soundToPlay);
  const clearSound = useAppStore((state) => state.clearSound);
  const allowSounds = useAppStore((state) => state.allowSounds);

  // Overlay and IAM state
  const currentOverlay = useExamSessionStore((state) => state.currentOverlay);
  const iam = useAppStore((state) => state.iam);
  // Use shallow equality to only re-render when sessionHeader value actually changes
  const sessionHeaderEnabled = useAppStore(
    (state) => state.enabledComponents.sessionHeader,
    (prev, next) => prev === next // Only re-render if the value changed
  );

  // Get sessionDetails defaultCollapsed setting from enabledComponents
  const sessionDetailsConfig = useAppStore((state) => state.enabledComponents.sessionDetails);
  const sessionDetailsDefaultCollapsed = typeof sessionDetailsConfig === 'object' && sessionDetailsConfig !== null
    ? (sessionDetailsConfig.defaultCollapsed ?? true)
    : true;

  // Check for debug URL parameter (memoized - URL doesn't change during component lifecycle)
  const isDebugMode = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('debug');
  }, []);

  // Debug logging - only runs when iam changes, not on every render
  useEffect(() => {
    if (isDebugMode) {
      console.log('Session IAM Details:', {
        type: typeof iam,
        isNull: iam === null,
        isUndefined: iam === undefined,
        hasStatement: iam?.hasOwnProperty('Statement'),
        statementIsArray: Array.isArray(iam?.Statement),
        statementLength: iam?.Statement?.length,
        keys: iam ? Object.keys(iam) : [],
        stringified: JSON.stringify(iam)
      });
    }
  }, [isDebugMode, iam]);

  const headerSize = useLayoutStore((state) => state.containers.header.size);

  const isFullScreen = useLayoutStore(
    (state) => state.isFullScreen
  );
  const isCollapsed = useLayoutStore(
    (state) => state?.detailsSplitView?.isCollapsed
  );

  // Draggable annotations modal state
  const annotationsModal = useDraggableModal({
    initialPosition: { x: 100, y: 100 },
    initialSize: { width: 900, height: 700 },
    initialOpen: false
  });

  // Session diagnostics modal state from AppStore
  const isDiagnosticsModalOpen = useAppStore((state) => state.isDiagnosticsModalOpen);
  const isTrackDebugModalOpen = useAppStore((state) => state.isTrackDebugModalOpen);

  const sessionRef = useRef(null);
  const videoContainerRef = useRef(null);
  const videoControlsRef = useRef(null);
  const sessionDetailsRef = useRef(null);

  // Apply audio output device to all media elements
  useAudioOutputDevice();

  // State for the vertical split (top/bottom)
  const [verticalSplit, setVerticalSplit] = useState(50);


  /**
   * Memoize configuration object to prevent unnecessary reinitializations
   * Only recreates when actual config values change
   */
  const configs = useMemo(() => ({
    apiUrl,
    roomId,
    sessionSid,
    examSid,
    installSid,
    isReconnect,
    identity,
    spToken,
    websocketUrl,
    enabledComponents,
    video,
    type,
    viewMode,
    magnification,
    defaultSessionDetailsTab,
    attachedToProctorRoomSid,
  }), [apiUrl, roomId, sessionSid, examSid, installSid, isReconnect, identity, spToken, websocketUrl, enabledComponents, video, type, viewMode, magnification, defaultSessionDetailsTab, attachedToProctorRoomSid]);

  /**
   * Initialize the session on mount
   * Calls AppStore.reinitialize which runs parallel initialization of all stores
   * See AppStore.initialize documentation for detailed initialization flow
   */
  const initializeSession = useCallback(async () => {
    await AppStore.reinitialize(configs, props, defaultSessionDetailsTab, attachedToProctorRoomSid);
  }, [configs, props, defaultSessionDetailsTab, attachedToProctorRoomSid]);

  useEffect(() => {
    initializeSession();

    // Cleanup function to clear EventsStore when component unmounts
    return () => {
      if (EventStore && EventStore.cleanup) {
        EventStore.cleanup();
      }
    };
  }, []);

  // Set enabled components ONLY on initial mount from props
  // After initialization, only publicApi methods (enableComponents/disableComponents) can update
  const enabledComponentsInitialized = useRef(false);
  useEffect(() => {
    if (initialized && enabledComponents && !enabledComponentsInitialized.current) {
      AppStore.updateEnabledComponents(enabledComponents);
      enabledComponentsInitialized.current = true;
    }
  }, [initialized, enabledComponents]);

  // Remove loading overlay after initialization and IAM validation
  useEffect(() => {
    if (initialized && iam) {
      // Delay to ensure UI has started rendering before removing overlay
      setTimeout(() => {
        ExamSessionStore.removeOverlay(CONSTANTS.OVERLAYS.SESSION.LOADING.NAME);
      }, 200);
    }
  }, [initialized, iam]);

  /**
   * Initialize layout and register DOM element references
   * Uses useLayoutEffect to ensure DOM measurements are accurate
   */
  useLayoutEffect(() => {
    if (sessionRef.current) {
      const elements = {
        session: sessionRef.current,
        video: videoContainerRef.current,
        controls: videoControlsRef.current,
        details: sessionDetailsRef.current,
      };

      LayoutStore.initialize(elements);
    }

    const handleResize = debounce(() => {
      LayoutStore.adjustSizing();
    }, 300);

    // Trigger session loaded event for external listeners
    if (initialized) {
      EventStore.trigger('onSessionLoaded', {
        roomId,
        sessionData: ExamSessionStore.sessionData,
        userData: ExamSessionStore.userData
      });

      // Trigger session ready event after component is fully mounted and ref is available
      // This ensures that the sessionViewerRef.current is populated
      setTimeout(() => {
        EventStore.trigger('onSessionReady', {
          roomId,
          sessionData: ExamSessionStore.sessionData,
          userData: ExamSessionStore.userData
        });
      }, 0);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [initialized]);


  // Update magnification in AppStore when prop changes
  useEffect(() => {
    if (magnification && AppStore) {
      AppStore.updateMagnification(magnification);
    }
  }, [magnification]);

  useEffect(() => {
    // If the session is not live, go ahead and set the times...
    if (sessionRef.current) {
      LayoutStore.setSessionRef(sessionRef.current);
    }
  }, [sessionRef]);

  // Register all layout keyboard shortcuts
  useLayoutHotkeys({
    setVerticalSplit,
    LayoutStore,
    PlayerStore,
    isCollapsed,
    annotationsModal,
  });

  /**
   * Expose public API methods via ref for external control
   * Allows parent components to control playback, access state, and manage settings
   * 
   * Available methods:
   * - play(), pause(), seek(time), setPlaybackRate(rate)
   * - getSessionState(), getPlayerState()
   * - updateSettings(settings)
   * - And more - see useSessionPublicApi for full API
   */
  const publicApi = useSessionPublicApi({
    ExamSessionStore,
    AppStore,
    SettingsStore,
    PlayerStore,
    LayoutStore,
    sessionSid,
    sessionRef
  });

  useImperativeHandle(ref, () => publicApi, [
    ExamSessionStore,
    AppStore,
    SettingsStore,
    PlayerStore,
    LayoutStore,
    sessionSid,
    sessionRef
  ]);

  /**
   * Memoize video player section to prevent unnecessary re-renders
   * Only recreates when sessionHeader state or header size changes
   */
  const RenderVideoPlayer = useMemo(() => {
    let height = `calc(100% - ${headerSize?.height + 3 || 0}px)`;
    if (!sessionHeaderEnabled) {
      height = "100%";
    }

    return (
      <>
        {sessionHeaderEnabled && <Header />}
        <View
          height={height}
          background="primary"
          as="div"
          overflowX="hidden"
          overflowY="hidden"
          padding="xx-small none none none"
          className="session-video-playback-container"
        >
          <MemoizedVideoPlayer focusedSessionSid={focusedSessionSid} />
        </View>
      </>
    );
  }, [sessionHeaderEnabled, headerSize, focusedSessionSid]);

  /**
   * Memoize overlay component to prevent unnecessary re-renders
   * Overlays are used for loading states, transfers, and error messages
   */
  const MemoizedOverlay = useMemo(() => {
    if (!currentOverlay) return null;

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 8000,
        }}
      >
        <SessionOverlay />
      </div>
    );
  }, [currentOverlay]);

  return (
    <InstUISettingsProvider>
      <div style={{ position: 'relative', height: '100%', width: '100%', overflow: 'hidden' }}>
        <View
          as="div"
          height="100%"
          className="session-viewer-container"
          background="primary"
          overflowY="hidden"
          overflowX="hidden"
          elementRef={(el) => {
            sessionRef.current = el;
          }}>
          <>
            {/* Main content - only renders when initialized and iam are present */}
            {(initialized && iam) && (
              <FullScreenProvider mountNode={sessionRef.current} isFullScreen={isFullScreen}>
                <IamValidationProvider
                  iam={iam}
                  withDebug={isDebugMode}
                  onError={(error, context) => {
                    // TODO: Integrate with platform logging system
                    console.error('IAM Validation Error:', error.message, context);
                  }}
                >
                  <IamValidationGuard fallback={<SessionOverlay />}>
                    <View
                      as="div"
                      className="main-split-view-wrapper"
                      height="100%"
                      background="primary"
                      overflowY="hidden"
                      overflowX="hidden"
                    >
                      <SplitView
                        direction="vertical"
                        background="primary"
                        tabIndex={2}
                        ref={(el) => {
                          // Store the SplitView reference in LayoutStore
                          if (el) {
                            LayoutStore.setDetailsSplitView(el);
                          }
                        }}
                        firstContent={RenderVideoPlayer}
                        secondContent={
                          <SessionDetailsWrapper sessionDetailsRef={sessionDetailsRef} />
                        }
                        initialSplit={50}
                        defaultSplit={50}
                        minFirstSize={10}
                        minSecondSize={20}
                        resizable={true}
                        collapsible={true}
                        collapseSize="43px"
                        collapsedPane="second"
                        initialCollapsed={sessionDetailsDefaultCollapsed}
                        splitPercentage={verticalSplit}
                        onChange={setVerticalSplit}
                      />

                    </View>
                    {/* Any modals or global items that need to be used throught the session... */}


                  </IamValidationGuard>
                </IamValidationProvider>
              </FullScreenProvider>
            )}

            {/* Overlay - shows on top when active */}
            {MemoizedOverlay}
          </>
        </View>

        {/* Draggable Annotations Modal */}
        <DraggableAnnotationsModal
          isOpen={annotationsModal.isOpen}
          onClose={annotationsModal.closeModal}
          initialPosition={annotationsModal.position}
          initialSize={annotationsModal.size}
          mountNode={sessionRef.current}
        />

        <SessionLogModal />
        <IncidentReportModal />
        <SettingsModal />
        <CheckinRequiredModal />
        <SessionDiagnostics
          isOpen={isDiagnosticsModalOpen}
          onClose={() => AppStore.closeDiagnosticsModal()}
        />
        <TrackDebugModal
          isOpen={isTrackDebugModalOpen}
          onClose={() => AppStore.closeTrackDebugModal()}
        />
        <Sounds
          soundToPlay={soundToPlay}
          clearSound={clearSound}
          allowSounds={allowSounds}
        />

      </div>
    </InstUISettingsProvider>
  );
});

ReactSession.displayName = 'ReactSession';
